// 3_pages/category_crossell.js
// Category and search listings: place product-SKU banners first (priority),
// then fill remaining MIN_GAP slots with cycling universal banners.

const DATA_PATH = "/user/documents/upload/shoptet_uploads/category_crossell/data.json";
const UNIVERSAL_KEY = "UNIVERSAL";
const MIN_GAP = 8;
const CATEGORY_LISTING_SELECTOR = "#products.products-block";
const SEARCH_LISTING_SELECTOR = ".raventic-search-results-products #products.products-block, .raventic-search-results-products-list + #products.products-block";
const PAIR_CLASS = "category-crossell-pair";
const BANNER_CLASS = "category-crossell";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const CACHE_PREFIX = "category_crossell_v3_";

let fetchPromise = null;
let moreProductsTimer = null;
let searchResultsTimer = null;
let renderToken = 0;
let listingObserver = null;
let knownProductCount = 0;
let universalCursor = 0;

export function init() {
	const body = document.body;
	if (!body.classList.contains("type-category") && !body.classList.contains("type-search")) {
		return;
	}

	renderCrossell({ mode: "full" });
	observeListing();

	document.addEventListener("ShoptetDOMContentLoaded", function () {
		renderCrossell({ mode: "full" });
		observeListing();
	});
	document.addEventListener("ShoptetDOMPageContentLoaded", function () {
		renderCrossell({ mode: "full" });
		observeListing();
	});
	document.addEventListener("ShoptetDOMPageMoreProductsLoaded", scheduleMoreProductsRender);
	document.addEventListener("RAVENTIC SEARCH RESULTS TRANSFORMED", function () {
		if (searchResultsTimer) clearTimeout(searchResultsTimer);
		searchResultsTimer = setTimeout(function () {
			searchResultsTimer = null;
			renderCrossell({ mode: "full" });
			observeListing();
		}, 50);
	});
}

function scheduleMoreProductsRender() {
	if (moreProductsTimer) clearTimeout(moreProductsTimer);
	moreProductsTimer = setTimeout(function () {
		moreProductsTimer = null;
		requestAnimationFrame(function () {
			renderCrossell({ mode: "more" });
		});
	}, 100);
}

function getListing() {
	if (document.body.classList.contains("type-search")) {
		return document.querySelector(SEARCH_LISTING_SELECTOR);
	}
	return document.querySelector(CATEGORY_LISTING_SELECTOR);
}

function observeListing() {
	const listing = getListing();
	if (!listing) return;

	knownProductCount = getListingProducts(listing).length;

	if (listingObserver) listingObserver.disconnect();
	listingObserver = new MutationObserver(function () {
		const count = getListingProducts(listing).length;
		if (count <= knownProductCount) return;
		knownProductCount = count;
		scheduleMoreProductsRender();
	});
	listingObserver.observe(listing, { childList: true, subtree: false });
}

function renderCrossell(options) {
	let mode = options && options.mode === "more" ? "more" : "full";
	const token = ++renderToken;

	loadData().then(function (data) {
		if (token !== renderToken) return;

		const listing = getListing();
		if (!listing) return;

		const items = data?.items;
		if (!items || typeof items !== "object") return;

		if (mode === "full" && listing.querySelector("." + BANNER_CLASS)) {
			mode = "more";
		}

		if (mode === "full") {
			cleanupListing(listing);
			universalCursor = 0;
		} else {
			syncUniversalCursor(listing);
		}

		const products = getListingProducts(listing);
		knownProductCount = products.length;
		if (!products.length) return;

		placeProductBanners(products, items, mode);
		placeUniversalBanners(products, data, mode);
		observeListing();
	});
}

function placeProductBanners(products, items, mode) {
	let lastProductBannerIndex = -MIN_GAP;

	products.forEach(function (product, index) {
		if (index === 0) return;
		if (productParentPair(product)) {
			lastProductBannerIndex = index;
			return;
		}

		const sku = getProductSku(product);
		if (!sku || sku === UNIVERSAL_KEY) return;

		const item = items[sku];
		if (!item) return;

		const placeIndex = resolveBannerPlaceIndex(index, products, lastProductBannerIndex);
		if (placeIndex < 0) return;

		const placeProduct = products[placeIndex];
		if (productParentPair(placeProduct)) return;

		const banner = buildBanner(item, false);
		if (!banner) return;

		removeAdjacentUniversal(placeProduct);
		wrapPair(placeProduct, banner);
		lastProductBannerIndex = placeIndex;
	});
}

function placeUniversalBanners(products, data, mode) {
	if (!getUniversalList(data).length) return;

	let lastBannerIndex = findLastBannerIndex(products);

	products.forEach(function (product, index) {
		if (index === 0) return;
		if (hasBannerAt(product)) {
			lastBannerIndex = Math.max(lastBannerIndex, index);
			return;
		}
		if (index - lastBannerIndex < MIN_GAP) return;

		const placeIndex = resolveBannerPlaceIndex(index, products, lastBannerIndex);
		if (placeIndex < 0) return;

		const placeProduct = products[placeIndex];
		if (hasBannerAt(placeProduct)) return;

		const universalItem = nextUniversal(data);
		if (!universalItem) return;

		const banner = buildBanner(universalItem, true);
		if (!banner) return;

		insertAfterProduct(placeProduct, banner);
		lastBannerIndex = placeIndex;
	});
}

function resolveBannerPlaceIndex(index, products, lastBannerIndex) {
	const maxIndex = products.length - 2;
	if (maxIndex < 1) return -1;

	const placeIndex = index > maxIndex ? maxIndex : index;
	if (placeIndex < 1) return -1;
	if (placeIndex - lastBannerIndex < MIN_GAP) return -1;
	return placeIndex;
}

function removeAdjacentUniversal(product) {
	const next = product.nextElementSibling;
	if (!next || !next.classList.contains(BANNER_CLASS)) return;
	if (!next.classList.contains(BANNER_CLASS + "--universal")) return;
	next.remove();
}

function getUniversalList(data) {
	const list = data?.universal;
	if (Array.isArray(list) && list.length) return list;

	const legacy = data?.items?.[UNIVERSAL_KEY];
	if (Array.isArray(legacy) && legacy.length) return legacy;
	if (legacy && typeof legacy === "object") return [legacy];
	return [];
}

function nextUniversal(data) {
	const list = getUniversalList(data);
	if (!list.length) return null;
	const item = list[universalCursor % list.length];
	universalCursor += 1;
	return item;
}

function syncUniversalCursor(listing) {
	const used = listing.querySelectorAll("." + BANNER_CLASS + "--universal").length;
	if (universalCursor < used) universalCursor = used;
}

function findLastBannerIndex(products) {
	let last = -MIN_GAP;
	products.forEach(function (product, index) {
		if (hasBannerAt(product)) last = index;
	});
	return last;
}

function hasBannerAt(product) {
	if (productParentPair(product)) return true;
	const next = product.nextElementSibling;
	return Boolean(next && next.classList.contains(BANNER_CLASS));
}

function productParentPair(product) {
	return product.parentElement?.classList.contains(PAIR_CLASS) ? product.parentElement : null;
}

function loadData() {
	if (fetchPromise) return fetchPromise;

	const storageKey = CACHE_PREFIX + location.host;
	const expiryKey = storageKey + "_expiry";
	const now = Date.now();

	try {
		const expiry = localStorage.getItem(expiryKey);
		const cached = localStorage.getItem(storageKey);
		if (expiry && cached && now < parseInt(expiry, 10)) {
			fetchPromise = Promise.resolve(JSON.parse(cached));
			return fetchPromise;
		}
		localStorage.removeItem(storageKey);
		localStorage.removeItem(expiryKey);
	} catch (error) {
		localStorage.removeItem(storageKey);
		localStorage.removeItem(expiryKey);
	}

	fetchPromise = fetch(DATA_PATH)
		.then(function (response) {
			if (!response.ok) throw new Error("Network response was not ok");
			return response.json();
		})
		.then(function (data) {
			try {
				localStorage.setItem(storageKey, JSON.stringify(data));
				localStorage.setItem(expiryKey, String(now + CACHE_TTL_MS));
			} catch (error) {}
			return data;
		})
		.catch(function (error) {
			console.warn("[NAT] category_crossell.js: failed to load data", error);
			fetchPromise = null;
			return null;
		});

	return fetchPromise;
}

function cleanupListing(listing) {
	listing.querySelectorAll("." + PAIR_CLASS).forEach(function (pair) {
		const product = pair.querySelector(":scope > .product");
		if (product) {
			pair.replaceWith(product);
		} else {
			pair.remove();
		}
	});
	listing.querySelectorAll("." + BANNER_CLASS).forEach(function (banner) {
		banner.remove();
	});
}

function getListingProducts(listing) {
	const products = [];
	Array.from(listing.children).forEach(function (child) {
		if (child.classList.contains("product")) {
			products.push(child);
			return;
		}
		if (child.classList.contains(PAIR_CLASS)) {
			const product = child.querySelector(":scope > .product");
			if (product) products.push(product);
		}
	});
	return products;
}

function getProductSku(product) {
	return product.querySelector("span[data-micro='sku']")?.textContent.trim() || "";
}

function wrapPair(product, banner) {
	const pair = document.createElement("div");
	pair.className = PAIR_CLASS;
	product.replaceWith(pair);
	pair.append(product, banner);
}

function insertAfterProduct(product, banner) {
	const host = productParentPair(product) || product;
	host.after(banner);
}

function isNonEmpty(value) {
	return typeof value === "string" && value.trim() !== "";
}

function buildBanner(item, universal) {
	const image = isNonEmpty(item.image) ? item.image.trim() : "";
	const tag = isNonEmpty(item.tag) ? item.tag.trim() : "";
	const text = isNonEmpty(item.text) ? item.text.trim() : "";
	const linkText = isNonEmpty(item.linkText) ? item.linkText.trim() : "";
	const link = isNonEmpty(item.link) ? item.link.trim() : "";
	const hasOverlay = Boolean(tag || text || linkText);

	if (!image && !hasOverlay) return null;

	const root = document.createElement(link ? "a" : "div");
	root.className = BANNER_CLASS;
	if (universal) root.classList.add(BANNER_CLASS + "--universal");
	if (image && hasOverlay) root.classList.add(BANNER_CLASS + "--has-fade");
	if (!image) root.classList.add(BANNER_CLASS + "--no-image");
	if (link) root.href = link;

	if (image) {
		const img = document.createElement("img");
		img.className = BANNER_CLASS + "__image";
		img.src = image;
		img.alt = text || tag || "";
		img.loading = "lazy";
		root.appendChild(img);
	}

	if (hasOverlay) {
		const body = document.createElement("div");
		body.className = BANNER_CLASS + "__body";

		if (tag) {
			const tagEl = document.createElement("span");
			tagEl.className = BANNER_CLASS + "__tag";
			tagEl.textContent = tag;
			body.appendChild(tagEl);
		}
		if (text) {
			const textEl = document.createElement("p");
			textEl.className = BANNER_CLASS + "__text";
			textEl.textContent = text;
			body.appendChild(textEl);
		}
		if (linkText) {
			const btn = document.createElement("span");
			btn.className = BANNER_CLASS + "__btn";
			btn.textContent = linkText;
			body.appendChild(btn);
		}

		root.appendChild(body);
	}

	return root;
}
